import {
    CheckCircleOutlined,
    DownloadOutlined,
    EditOutlined,
    EyeOutlined,
    LockOutlined,
    SearchOutlined,
    StarOutlined,
    TeamOutlined,
    TrophyOutlined,
    UnlockOutlined,
    UserOutlined,
    MinusCircleOutlined,
    WarningOutlined,
    PlayCircleOutlined,
    MailOutlined,
    FileDoneOutlined,
    FileZipOutlined,
} from '@ant-design/icons';
import {
    Alert,
    Avatar,
    Badge,
    Button,
    Card,
    Col,
    Descriptions,
    Empty,
    Input,
    Modal,
    InputNumber,
    Layout,
    List,
    message,
    Popconfirm,
    Progress,
    Row,
    Select,
    Space,
    Spin,
    Statistic,
    Table,
    Tabs,
    Tag,
    Tooltip,
    Typography
} from 'antd';
import dayjs from 'dayjs';
import {
    collection,
    doc,
    getDocs,
    query,
    serverTimestamp,
    updateDoc,
    where,
    writeBatch,
    deleteField,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import apis from '../../apis';
import {
    ageCategories,
    brassAgeCategoriesSolo,
    competitionList,
    electoneAgeCategoriesSolo,
    ensembleAgeCategories,
    guitarAgeCategoriesSolo,
    guzhengAgeCategoriesEnsemble,
    guzhengAgeCategoriesSolo,
    harpAgeCategoriesSolo,
    percussionAgeCategoriesSolo,
    stringAgeCategoriesSolo,
    vocalAgeCategoriesSolo,
    woodwinAgeCategoriesSolo,
} from '../../constant/RegisterPageConst';
import { useAuth } from '../../context/DataContext';
import { db } from '../../firebase';
import {
    calculateAward,
    calculateRegistrantScoreAndAward,
    getEffectiveScore,
    isAwardSyncNeeded,
} from '../../utils/scoringCalculator';
import { fetchVideoPenaltyConfig } from '../../utils/videoPenaltyConfig';
import { formatDuration, getVideoDurationSeconds } from '../../utils/sessionDuration';
import {
    formatCommentSheetAward,
    formatCommentSheetScore,
    formatCommentSheetCategory,
    toJurySlug,
    getPerformerName,
    getSongName,
} from '../../utils/csvVariableSafeUtil';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

/**
 * Get teacher name from registrant data.
 */
const getTeacherName = (reg) => {
    const teacher = reg?.teacherName || reg?.teacher || (reg?.userType === 'Teacher' ? reg?.name : '') || '';
    return teacher.trim();
};

/**
 * Combined map of all age categories across all instruments
 */
const allAgeCategoriesMap = {
    ...ageCategories,
    ...ensembleAgeCategories,
    ...stringAgeCategoriesSolo,
    ...vocalAgeCategoriesSolo,
    ...woodwinAgeCategoriesSolo,
    ...percussionAgeCategoriesSolo,
    ...guitarAgeCategoriesSolo,
    ...brassAgeCategoriesSolo,
    ...electoneAgeCategoriesSolo,
    ...harpAgeCategoriesSolo,
    ...guzhengAgeCategoriesSolo,
    ...guzhengAgeCategoriesEnsemble,
};

/**
 * Map age category key to full description
 */
const getAgeCategoryLabel = (ageCategoryKey) => {
    if (!ageCategoryKey) return '';
    return allAgeCategoriesMap[ageCategoryKey] || ageCategoryKey;
};

/**
 * Get category classification for a registrant (Ensemble or age category label)
 */
const getRegistrantCategory = (reg) => {
    if (reg?.PerformanceCategory === 'Ensemble') {
        return 'Ensemble';
    }
    return getAgeCategoryLabel(reg?.ageCategory) || reg?.ageCategory || 'Unassigned';
};

/**
 * Color code for scores.
 */
const getScoreColor = (score) => {
    if (score === null || score === undefined) return '#999';
    if (score >= 90) return '#52c41a';
    if (score >= 75) return '#faad14';
    if (score >= 60) return '#ff7a45';
    return '#ff4d4f';
};

/**
 * ScoringRecap — Admin page to review, adjust, and finalize all jury scores.
 */
const ScoringRecap = () => {
    const { t } = useTranslation();
    const { loggedInUser, currentEventId } = useAuth();

    // State
    const [selectedEvent, setSelectedEvent] = useState(currentEventId || 'APCS2026');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [activeCategory, setActiveCategory] = useState('All');
    const [registrants, setRegistrants] = useState([]);
    const [juries, setJuries] = useState([]); // juries for the selected category
    const [allScores, setAllScores] = useState([]); // flat array of all JuryScores2025 docs
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [adminNoteFilter, setAdminNoteFilter] = useState('All');
    const [selectedJuryFilter, setSelectedJuryFilter] = useState(null);
    
    const [videoPenaltyConfig, setVideoPenaltyConfig] = useState(null);

    // Admin Note Modal State
    const [isAdminNoteModalVisible, setIsAdminNoteModalVisible] = useState(false);
    const [adminNoteRecord, setAdminNoteRecord] = useState(null);
    const [adminNoteValue, setAdminNoteValue] = useState('');
    const [savingAdminNote, setSavingAdminNote] = useState(false);

    // Editing state
    const [editingKey, setEditingKey] = useState(null); // scoreDocId being edited
    const [editingValue, setEditingValue] = useState(null);
    const [savingEdit, setSavingEdit] = useState(false);

    // Comment editing state
    const [editingCommentKey, setEditingCommentKey] = useState(null); // scoreDocId for comment being edited
    const [editingCommentValue, setEditingCommentValue] = useState('');
    const [editingPanelCommentKey, setEditingPanelCommentKey] = useState(null);
    const [editingPanelCommentValue, setEditingPanelCommentValue] = useState('');
    const [savingComment, setSavingComment] = useState(false);

    // Finalization state
    const [finalizingId, setFinalizingId] = useState(null);

    // Batch export state
    const [batchExporting, setBatchExporting] = useState(false);

    // Video Modal
    const [videoModalOpen, setVideoModalOpen] = useState(false);
    const [currentVideo, setCurrentVideo] = useState(null);

    // Category options
    const categoryOptions = Object.values(competitionList).map((cat) => ({
        label: cat,
        value: cat,
    }));

    // Fetch data when category or event changes
    useEffect(() => {
        if (selectedCategory && selectedEvent) {
            fetchData(selectedCategory, selectedEvent);
        }
    }, [selectedCategory, selectedEvent]);

    const fetchData = async (category, eventId) => {
        setLoading(true);
        try {
            try {
                const eventVideoPenaltyConfig = await fetchVideoPenaltyConfig(eventId);
                setVideoPenaltyConfig(eventVideoPenaltyConfig);
            } catch (configError) {
                console.warn(`Could not load video penalty configuration for ${eventId}:`, configError);
                setVideoPenaltyConfig(null);
            }

            // Fetch registrants for this category
            const regQuery = query(
                collection(db, 'Registrants2025'),
                where('eventId', '==', eventId),
                where('competitionCategory', '==', category)
            );
            const regSnap = await getDocs(regQuery);
            const regs = regSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

            // Fetch ALL jury scores for this category (not filtered by juryUserId)
            const scoresQuery = query(
                collection(db, 'JuryScores2025'),
                where('competitionCategory', '==', category)
            );
            const scoresSnap = await getDocs(scoresQuery);
            const scores = scoresSnap.docs.map((d) => ({
                docId: d.id,
                ...d.data(),
                timestamp: d.data().timestamp?.toDate?.() || null,
            }));

            // Fetch juries for this category
            const juriesQuery = query(
                collection(db, 'users'),
                where('role', '==', 'jury'),
                where('competitionCategory', '==', category)
            );
            const juriesSnap = await getDocs(juriesQuery);
            const juriesData = juriesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

            setRegistrants(regs);
            setAllScores(scores);
            setJuries(juriesData);
        } catch (error) {
            console.error('Error fetching scoring data:', error);
            message.error('Failed to load scoring data');
        } finally {
            setLoading(false);
        }
    };

    // Group scores by registrantId
    const scoresByRegistrant = useMemo(() => {
        const map = {};
        allScores.forEach((s) => {
            if (!map[s.registrantId]) map[s.registrantId] = [];
            map[s.registrantId].push(s);
        });
        return map;
    }, [allScores]);

    // Build table data
    const tableData = useMemo(() => {
        return registrants.map((reg) => {
            const scores = scoresByRegistrant[reg.id] || [];
            const scoring = calculateRegistrantScoreAndAward(scores, reg, videoPenaltyConfig);
            const isFinalized = scores.length > 0 && scores.every((s) => s.isFinalized === true);
            const isSomeFinalized = scores.some((s) => s.isFinalized === true);

            let status;
            if (isFinalized) status = 'Finalized';
            else if (scores.length === 0) status = 'Not Scored';
            else status = 'Scored';

            const teacher = getTeacherName(reg);
            const songName = getSongName(reg);

            return {
                key: reg.id,
                registrant: reg,
                name: getPerformerName(reg),
                teacherName: teacher || '—',
                songName: songName || '—',
                adminNote: reg.adminNote || '',
                ageCategory: reg.PerformanceCategory === 'Ensemble'
                    ? 'Ensemble'
                    : (getAgeCategoryLabel(reg.ageCategory) || reg.ageCategory || '—'),
                performanceCategory: reg.PerformanceCategory || 'Solo',
                scores,
                originalAverage: scoring.rawAverage,
                manualPenaltyScore: scoring.manualPenaltyScore,
                manualPenaltyReason: scoring.manualPenaltyReason,
                autoPenaltyScore: scoring.autoPenaltyScore,
                autoPenaltyReason: scoring.autoPenaltyReason,
                totalPenaltyScore: scoring.totalPenaltyScore,
                videoPenaltyRuleStatus: scoring.videoPenaltyRuleStatus,
                videoPenaltyMaximumMinutes: scoring.videoPenaltyMaximumMinutes,
                needsAwardSync: isAwardSyncNeeded(reg, scoring),
                average: scoring.finalAverageScore,
                award: scoring.finalAward,
                status,
                isFinalized,
                isSomeFinalized,
                juryCount: scores.length,
            };
        });
    }, [registrants, scoresByRegistrant, videoPenaltyConfig]);

    // Derive age categories from data for the selected competition category (like in JuryDashboard)
    const categories = useMemo(() => {
        const cats = new Set();
        registrants.forEach((reg) => {
            if (reg.PerformanceCategory === 'Ensemble') {
                cats.add('Ensemble');
            } else {
                const label = getAgeCategoryLabel(reg.ageCategory);
                if (label) {
                    cats.add(label);
                } else if (reg.ageCategory) {
                    cats.add(reg.ageCategory);
                }
            }
        });
        const sortedCats = Array.from(cats).sort((a, b) => {
            if (a === 'Ensemble') return 1;
            if (b === 'Ensemble') return -1;
            return a.localeCompare(b);
        });
        return ['All', ...sortedCats];
    }, [registrants]);

    // Keep activeCategory valid when category list updates
    useEffect(() => {
        if (activeCategory !== 'All' && !categories.includes(activeCategory)) {
            setActiveCategory('All');
        }
    }, [categories, activeCategory]);

    // Counts per category (total, not scored, finalized)
    const categoryCounts = useMemo(() => {
        const out = {
            total: { All: tableData.length },
            notScored: { All: 0 },
            finalized: { All: 0 },
        };
        categories.forEach((c) => {
            if (c === 'All') return;
            out.total[c] = 0;
            out.notScored[c] = 0;
            out.finalized[c] = 0;
        });

        tableData.forEach((row) => {
            const cat = getRegistrantCategory(row.registrant);

            if (row.status === 'Not Scored') {
                out.notScored.All += 1;
                if (out.notScored[cat] !== undefined) out.notScored[cat] += 1;
            }
            if (row.status === 'Finalized') {
                out.finalized.All += 1;
                if (out.finalized[cat] !== undefined) out.finalized[cat] += 1;
            }
            if (out.total[cat] !== undefined) {
                out.total[cat] += 1;
            }
        });

        return out;
    }, [tableData, categories]);

    // Filter
    const filteredData = useMemo(() => {
        const q = searchText.trim().toLowerCase();
        return tableData.filter((row) => {
            // Age Category filter (like in JuryDashboard)
            if (activeCategory !== 'All') {
                const cat = getRegistrantCategory(row.registrant);
                if (cat !== activeCategory) return false;
            }
            if (statusFilter !== 'All') {
                if (statusFilter === 'Scored' && row.status !== 'Scored') return false;
                if (statusFilter === 'Not Scored' && row.status !== 'Not Scored') return false;
                if (statusFilter === 'Finalized' && row.status !== 'Finalized') return false;
            }
            if (adminNoteFilter !== 'All') {
                const hasNote = Boolean(row.adminNote && row.adminNote.trim() !== '');
                if (adminNoteFilter === 'NOT_EMPTY' && !hasNote) return false;
                if (adminNoteFilter === 'EMPTY' && hasNote) return false;
            }
            if (
                q &&
                !row.name.toLowerCase().includes(q) &&
                !(row.teacherName && row.teacherName !== '—' && row.teacherName.toLowerCase().includes(q)) &&
                !(row.adminNote && row.adminNote.toLowerCase().includes(q)) &&
                !(row.songName && row.songName !== '—' && row.songName.toLowerCase().includes(q))
            ) return false;
            if (selectedJuryFilter && !row.scores.some(s => s.juryUserId === selectedJuryFilter)) return false;
            return true;
        });
    }, [tableData, searchText, statusFilter, adminNoteFilter, selectedJuryFilter, activeCategory]);

    // Stats
    const stats = useMemo(() => ({
        total: tableData.length,
        scored: tableData.filter((r) => r.status === 'Scored').length,
        notScored: tableData.filter((r) => r.status === 'Not Scored').length,
        finalized: tableData.filter((r) => r.status === 'Finalized').length,
    }), [tableData]);

    // Jury Progress
    const juryProgress = useMemo(() => {
        return juries.map((jury) => {
            const juryName = (jury.name || '').trim().toLowerCase();
            
            // Registrants this jury needs to score (PAID, and not their own student)
            const targetRegistrants = registrants.filter((reg) => {
                const isPaid = reg.paymentStatus === 'PAID' || (reg.invoiceStatus && reg.invoiceStatus.toLowerCase() === 'paid');
                const regTeacher = getTeacherName(reg).toLowerCase();
                const isOwnStudent = juryName && (regTeacher === juryName);
                return isPaid && !isOwnStudent;
            });
            
            const targetCount = targetRegistrants.length;
            const scoredCount = allScores.filter(s => s.juryUserId === jury.id).length;
            const percent = targetCount > 0 ? Math.round((scoredCount / targetCount) * 100) : (scoredCount > 0 ? 100 : 0);
            
            return {
                id: jury.id,
                name: jury.name || jury.email || 'Unknown',
                targetCount,
                scoredCount,
                percent,
                isComplete: targetCount > 0 && scoredCount >= targetCount,
            };
        }).sort((a, b) => b.percent - a.percent); // Sort by highest percent first
    }, [juries, registrants, allScores]);

    // --- Admin edit score ---
    const handleStartEdit = (scoreDocId, currentAdminScore, originalScore) => {
        setEditingKey(scoreDocId);
        setEditingValue(currentAdminScore ?? originalScore);
    };

    const handleSaveEdit = async (scoreDocId) => {
        if (editingValue === null || editingValue === undefined) {
            message.warning('Please enter a score');
            return;
        }
        if (editingValue < 0 || editingValue > 100) {
            message.error('Score must be between 0 and 100');
            return;
        }
        setSavingEdit(true);
        try {
            const docRef = doc(db, 'JuryScores2025', scoreDocId);
            await updateDoc(docRef, {
                adminAdjustedScore: Number(editingValue),
                adminAdjustedAt: serverTimestamp(),
                adminAdjustedBy: loggedInUser?.email || 'admin',
            });
            // Update local state
            setAllScores((prev) =>
                prev.map((s) =>
                    s.docId === scoreDocId
                        ? {
                            ...s,
                            adminAdjustedScore: Number(editingValue),
                            adminAdjustedAt: new Date(),
                            adminAdjustedBy: loggedInUser?.email || 'admin',
                        }
                        : s
                )
            );
            setEditingKey(null);
            setEditingValue(null);
            message.success('Score adjusted successfully');
        } catch (error) {
            console.error('Error saving admin score:', error);
            message.error('Failed to save score adjustment');
        } finally {
            setSavingEdit(false);
        }
    };

    const handleRemoveAdjustment = async (scoreDocId) => {
        setSavingEdit(true);
        try {
            const docRef = doc(db, 'JuryScores2025', scoreDocId);
            await updateDoc(docRef, {
                adminAdjustedScore: deleteField(),
                adminAdjustedAt: deleteField(),
                adminAdjustedBy: deleteField(),
            });
            // Update local state
            setAllScores((prev) =>
                prev.map((s) => {
                    if (s.docId === scoreDocId) {
                        const newS = { ...s };
                        delete newS.adminAdjustedScore;
                        delete newS.adminAdjustedAt;
                        delete newS.adminAdjustedBy;
                        return newS;
                    }
                    return s;
                })
            );
            setEditingKey(null);
            setEditingValue(null);
            message.success('Admin adjustment removed');
        } catch (error) {
            console.error('Error removing admin score:', error);
            message.error('Failed to remove score adjustment');
        } finally {
            setSavingEdit(false);
        }
    };

    const handleCancelEdit = () => {
        setEditingKey(null);
        setEditingValue(null);
    };

    // --- Comment edit handlers ---
    const handleStartEditComment = (scoreDocId, currentComment) => {
        setEditingCommentKey(scoreDocId);
        setEditingCommentValue(currentComment || '');
    };

    const handleSaveComment = async (scoreDocId) => {
        setSavingComment(true);
        try {
            const docRef = doc(db, 'JuryScores2025', scoreDocId);
            await updateDoc(docRef, {
                comment: editingCommentValue,
                commentEditedAt: serverTimestamp(),
                commentEditedBy: loggedInUser?.email || 'admin',
            });
            setAllScores((prev) =>
                prev.map((s) =>
                    s.docId === scoreDocId
                        ? { ...s, comment: editingCommentValue }
                        : s
                )
            );
            setEditingCommentKey(null);
            setEditingCommentValue('');
            message.success('Performance feedback updated');
        } catch (error) {
            console.error('Error saving comment:', error);
            message.error('Failed to save feedback');
        } finally {
            setSavingComment(false);
        }
    };

    const handleCancelEditComment = () => {
        setEditingCommentKey(null);
        setEditingCommentValue('');
    };

    const handleStartEditPanelComment = (scoreDocId, currentComment) => {
        setEditingPanelCommentKey(scoreDocId);
        setEditingPanelCommentValue(currentComment || '');
    };

    const handleSavePanelComment = async (scoreDocId) => {
        setSavingComment(true);
        try {
            const docRef = doc(db, 'JuryScores2025', scoreDocId);
            await updateDoc(docRef, {
                panelComment: editingPanelCommentValue,
                panelCommentEditedAt: serverTimestamp(),
                panelCommentEditedBy: loggedInUser?.email || 'admin',
            });
            setAllScores((prev) =>
                prev.map((s) =>
                    s.docId === scoreDocId
                        ? { ...s, panelComment: editingPanelCommentValue }
                        : s
                )
            );
            setEditingPanelCommentKey(null);
            setEditingPanelCommentValue('');
            message.success('APCS notes updated');
        } catch (error) {
            console.error('Error saving panel comment:', error);
            message.error('Failed to save notes');
        } finally {
            setSavingComment(false);
        }
    };

    const handleCancelEditPanelComment = () => {
        setEditingPanelCommentKey(null);
        setEditingPanelCommentValue('');
    };

    // --- Penalty ---
    const [penalizingId, setPenalizingId] = useState(null);

    const handleApplyPenalty = async (registrantId, points = 10) => {
        setPenalizingId(registrantId);
        try {
            const docRef = doc(db, 'Registrants2025', registrantId);
            await updateDoc(docRef, {
                penaltyScore: points,
                penaltyReason: 'Reading a book during performance',
                penaltyAppliedAt: serverTimestamp(),
                penaltyAppliedBy: loggedInUser?.email || 'admin'
            });
            setRegistrants(prev => prev.map(r => r.id === registrantId ? {
                ...r,
                penaltyScore: points,
                penaltyReason: 'Reading a book during performance'
            } : r));
            message.success(`Penalty of -${points} applied`);
        } catch (error) {
            console.error('Error applying penalty:', error);
            message.error('Failed to apply penalty');
        } finally {
            setPenalizingId(null);
        }
    };

    const handleRemovePenalty = async (registrantId) => {
        setPenalizingId(registrantId);
        try {
            const docRef = doc(db, 'Registrants2025', registrantId);
            await updateDoc(docRef, {
                penaltyScore: deleteField(),
                penaltyReason: deleteField(),
                penaltyAppliedAt: deleteField(),
                penaltyAppliedBy: deleteField()
            });
            setRegistrants(prev => prev.map(r => {
                if (r.id === registrantId) {
                    const newR = { ...r };
                    delete newR.penaltyScore;
                    delete newR.penaltyReason;
                    return newR;
                }
                return r;
            }));
            message.success('Penalty removed');
        } catch (error) {
            console.error('Error removing penalty:', error);
            message.error('Failed to remove penalty');
        } finally {
            setPenalizingId(null);
        }
    };

    // --- Admin Note Handlers ---
    const handleOpenAdminNoteModal = (registrant, currentNote) => {
        setAdminNoteRecord(registrant);
        setAdminNoteValue(currentNote !== undefined ? currentNote : (registrant?.adminNote || ''));
        setIsAdminNoteModalVisible(true);
    };

    const handleSaveAdminNote = async () => {
        if (!adminNoteRecord) return;
        setSavingAdminNote(true);
        try {
            const docRef = doc(db, 'Registrants2025', adminNoteRecord.id);
            await updateDoc(docRef, { adminNote: adminNoteValue });
            message.success('Admin Note updated successfully');
            setRegistrants((prev) =>
                prev.map((r) =>
                    r.id === adminNoteRecord.id
                        ? { ...r, adminNote: adminNoteValue }
                        : r
                )
            );
            setIsAdminNoteModalVisible(false);
            setAdminNoteRecord(null);
        } catch (error) {
            console.error('Error updating admin note:', error);
            message.error('Failed to update admin note.');
        } finally {
            setSavingAdminNote(false);
        }
    };

    // --- Finalize / Unfinalize ---
    const handleToggleFinalize = async (registrantId, shouldFinalize) => {
        const scoreDocs = scoresByRegistrant[registrantId] || [];
        if (scoreDocs.length === 0) {
            message.warning('No scores to finalize for this registrant');
            return;
        }
        setFinalizingId(registrantId);
        try {
            const batch = writeBatch(db);
            scoreDocs.forEach((s) => {
                const docRef = doc(db, 'JuryScores2025', s.docId);
                batch.update(docRef, {
                    isFinalized: shouldFinalize,
                    ...(shouldFinalize
                        ? {
                            finalizedAt: serverTimestamp(),
                            finalizedBy: loggedInUser?.email || 'admin',
                        }
                        : {
                            finalizedAt: null,
                            finalizedBy: null,
                        }),
                });
            });
            await batch.commit();

            // Update local state
            setAllScores((prev) =>
                prev.map((s) =>
                    s.registrantId === registrantId
                        ? {
                            ...s,
                            isFinalized: shouldFinalize,
                            finalizedAt: shouldFinalize ? new Date() : null,
                            finalizedBy: shouldFinalize ? (loggedInUser?.email || 'admin') : null,
                        }
                        : s
                )
            );
            message.success(
                shouldFinalize
                    ? 'Score finalized — jury can no longer edit'
                    : 'Score unfinalized — jury can now edit again'
            );
        } catch (error) {
            console.error('Error finalizing scores:', error);
            message.error('Failed to update finalization status');
        } finally {
            setFinalizingId(null);
        }
    };

    const handleWatchVideo = (s3Link) => {
        if (!s3Link) {
            message.warning("No video link provided for this registrant.");
            return;
        }

        const CLOUDFRONT_DOMAIN = 'https://dk423kvk3sha9.cloudfront.net';
        let cloudFrontUrl = s3Link;

        try {
            if (s3Link.startsWith('http') || s3Link.startsWith('s3://')) {
                // If it's a full S3 URL or s3:// URI, extract the path and append to CloudFront
                const urlObj = new URL(s3Link);
                cloudFrontUrl = `${CLOUDFRONT_DOMAIN}${urlObj.pathname}`;
            } else {
                // If it's just the key (e.g. 'videos/123.mp4')
                const prefix = s3Link.startsWith('/') ? '' : '/';
                cloudFrontUrl = `${CLOUDFRONT_DOMAIN}${prefix}${s3Link}`;
            }

            setCurrentVideo(cloudFrontUrl);
            setVideoModalOpen(true);
        } catch (error) {
            console.error("Failed to load video", error);
            message.error("Could not load video file.");
        }
    };

    // --- CSV Export ---
    const handleSendReminder = async () => {
        try {
            message.loading({ content: 'Sending reminder...', key: 'sendReminder' });
            await apis.email.sendEmailJuryReminderDummy({ email: 'renaldolouis555@gmail.com' });
            message.success({ content: 'Reminder sent!', key: 'sendReminder', duration: 2 });
        } catch (error) {
            console.error('Error sending reminder:', error);
            message.error({ content: 'Failed to send reminder', key: 'sendReminder', duration: 2 });
        }
    };

    // Standard CSV escaping (RFC 4180)
    const escapeCSV = (val, forceQuote = false) => {
        if (val === null || val === undefined) return '';
        const str = String(val);
        if (forceQuote || str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
            return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
    };

    // Helper to extract and sort distinct juries for exports
    const getExportJuries = (targetJuries = juries, targetData = filteredData) => {
        const juryMap = new Map();

        // 1. Check juries from category state
        (targetJuries || []).forEach((j) => {
            const name = (j.name || '').trim();
            if (name) {
                const slug = toJurySlug(name);
                if (slug) juryMap.set(slug, name);
            }
        });

        // 2. Also check any scores in targetData
        (targetData || []).forEach((row) => {
            (row.scores || []).forEach((s) => {
                const name = (s.juryName || '').trim();
                if (name && name !== 'Unknown') {
                    const slug = toJurySlug(name);
                    if (slug && !juryMap.has(slug)) {
                        juryMap.set(slug, name);
                    }
                }
            });
        });

        // 3. Fallback: if no names found in scores/juries, check juryUserId
        if (juryMap.size === 0) {
            (targetData || []).forEach((row) => {
                (row.scores || []).forEach((s) => {
                    if (s.juryUserId) {
                        const slug = toJurySlug(s.juryUserId);
                        if (slug && !juryMap.has(slug)) {
                            juryMap.set(slug, s.juryUserId);
                        }
                    }
                });
            });
        }

        // Sort juries alphabetically by display name
        const sortedJurySlugs = Array.from(juryMap.keys()).sort((a, b) => {
            const nameA = juryMap.get(a) || a;
            const nameB = juryMap.get(b) || b;
            return nameA.localeCompare(nameB);
        });

        return { toJurySlug, juryMap, sortedJurySlugs };
    };

    const handleExportCSV = ({ isVariableSafe = false } = {}) => {
        if (filteredData.length === 0) {
            message.warning('No data to export');
            return;
        }

        const { toJurySlug: slugFn, juryMap, sortedJurySlugs } = getExportJuries(juries, filteredData);

        // Build CSV headers based on export mode
        let headers;
        if (isVariableSafe) {
            headers = [
                'name',
                'award',
                'category',
                'score',
                ...sortedJurySlugs.map((slug, idx) => `header_${slug || (idx + 1)}`)
            ];
        } else {
            headers = [
                'no',
                'participants_details',
                ...sortedJurySlugs.map((slug) => `performance_feedback_${slug}`),
                'final_score',
                'award',
                'agecategory'
            ];
        }

        // Build CSV data rows
        const rows = filteredData.map((row, index) => {
            const no = index + 1;
            const participantsDetails = row.name && row.name !== 'Unknown' ? row.name : (getPerformerName(row.registrant) || row.registrant?.name || '');

            // Feedback for each sorted jury
            const feedbackValues = sortedJurySlugs.map((slug) => {
                const s = (row.scores || []).find((score) => {
                    const sSlug = slugFn(score.juryName);
                    return sSlug === slug;
                });
                const comment = s ? (s.comment || '').trim() : '';
                return escapeCSV(comment, comment.length > 0);
            });

            if (isVariableSafe) {
                // Feedback for each sorted jury with line breaks sanitized to spaces
                // (Illustrator's CSV parser fails on multiline cells with "The incoming variable library is invalid")
                const feedbackValues = sortedJurySlugs.map((slug) => {
                    const s = (row.scores || []).find((score) => {
                        const sSlug = slugFn(score.juryName);
                        return sSlug === slug;
                    });
                    const rawComment = s ? (s.comment || '').trim() : '';
                    const cleanComment = rawComment.replace(/[\r\n]+/g, ' ').trim();
                    return escapeCSV(cleanComment, cleanComment.length > 0);
                });

                const name = participantsDetails;
                const award = formatCommentSheetAward(row.award);
                const category = formatCommentSheetCategory(row, selectedCategory);
                const score = formatCommentSheetScore(row.average);

                const rowData = [
                    escapeCSV(name),
                    escapeCSV(award),
                    escapeCSV(category),
                    score,
                    ...feedbackValues
                ];
                return rowData.join(',');
            }

            const finalScore = row.average !== null && row.average !== undefined ? Number(row.average) : '';
            const award = row.award && row.award !== 'N/A' ? row.award : '';
            const ageCategory = row.ageCategory && row.ageCategory !== '—' ? row.ageCategory : '';

            const rowData = [
                no,
                escapeCSV(participantsDetails),
                ...feedbackValues,
                finalScore,
                escapeCSV(award),
                escapeCSV(ageCategory)
            ];

            return rowData.join(',');
        });

        const csv = [headers.join(','), ...rows].join('\n');
        // Do NOT prepend UTF-8 BOM (\uFEFF) for Comment Sheet CSV (Illustrator treats \uFEFF as an invalid variable char \uFEFFname)
        const blobContent = isVariableSafe ? csv : ('\uFEFF' + csv);
        const blob = new Blob([blobContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;

        const dateStr = dayjs().format('YYYY-MM-DD');
        if (isVariableSafe) {
            const catUpper = (selectedCategory || 'ALL').replace(/[^a-zA-Z0-9]+/g, '_').toUpperCase();
            const ageUpper = activeCategory !== 'All' ? `_${activeCategory.replace(/[^a-zA-Z0-9]+/g, '_').toUpperCase()}` : '';
            a.download = `DESIGN_TEAM_${catUpper}${ageUpper}_${dateStr}_COMMENT_SHEET.csv`;
        } else {
            const categorySlug = activeCategory !== 'All' ? `_${toJurySlug(activeCategory)}` : '';
            a.download = `scoring_recap_${toJurySlug(selectedCategory)}${categorySlug}_${dateStr}.csv`;
        }

        a.click();
        URL.revokeObjectURL(url);
        message.success(isVariableSafe ? 'Comment Sheet CSV exported successfully' : 'CSV exported successfully');
    };

    /**
     * Batch export Comment Sheet CSVs across ALL competition categories for the selected event.
     * Splits finalized winners into award tier subfolders (Sapphire, Diamond, Gold, Silver)
     * packaged in a single ZIP file.
     */
    const handleExportAllWinnersByAward = async () => {
        if (!selectedEvent) {
            message.warning('Please select an event first');
            return;
        }

        setBatchExporting(true);
        message.loading({
            content: 'Fetching and compiling winner comment sheets for all categories...',
            key: 'winnerExportZip',
            duration: 0,
        });

        try {
            // 1. Fetch video penalty configuration for the event
            let eventVideoPenaltyConfig = null;
            try {
                eventVideoPenaltyConfig = await fetchVideoPenaltyConfig(selectedEvent);
            } catch (cfgErr) {
                console.warn(`Could not load video penalty configuration for ${selectedEvent}:`, cfgErr);
            }

            const categories = Object.values(competitionList);
            const zip = new JSZip();
            let totalWinnersFound = 0;
            let totalFilesAdded = 0;

            const dateStr = dayjs().format('YYYY-MM-DD');
            const targetTiers = ['Sapphire', 'Diamond', 'Gold', 'Silver', 'Fail'];

            // 2. Process all competition categories in parallel
            await Promise.all(
                categories.map(async (category) => {
                    try {
                        // Fetch registrants for this category and event
                        const regQuery = query(
                            collection(db, 'Registrants2025'),
                            where('eventId', '==', selectedEvent),
                            where('competitionCategory', '==', category)
                        );
                        const regSnap = await getDocs(regQuery);
                        const catRegs = regSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
                        if (catRegs.length === 0) return;

                        // Fetch jury scores for this category
                        const scoresQuery = query(
                            collection(db, 'JuryScores2025'),
                            where('competitionCategory', '==', category)
                        );
                        const scoresSnap = await getDocs(scoresQuery);
                        const catScores = scoresSnap.docs.map((d) => ({
                            docId: d.id,
                            ...d.data(),
                            timestamp: d.data().timestamp?.toDate?.() || null,
                        }));

                        // Fetch juries for this category
                        const juriesQuery = query(
                            collection(db, 'users'),
                            where('role', '==', 'jury'),
                            where('competitionCategory', '==', category)
                        );
                        const juriesSnap = await getDocs(juriesQuery);
                        const catJuries = juriesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

                        // Group scores by registrantId
                        const scoresByReg = {};
                        catScores.forEach((s) => {
                            if (!scoresByReg[s.registrantId]) scoresByReg[s.registrantId] = [];
                            scoresByReg[s.registrantId].push(s);
                        });

                        // Calculate scores and filter finalized participants (Sapphire, Diamond, Gold, Silver, Fail)
                        const evaluatedWinners = [];
                        catRegs.forEach((reg) => {
                            const scores = scoresByReg[reg.id] || [];
                            const scoring = calculateRegistrantScoreAndAward(scores, reg, eventVideoPenaltyConfig);
                            const isFinalized = scores.length > 0 && scores.every((s) => s.isFinalized === true);

                            // Exclude unfinalized, unscored, N/A
                            if (!isFinalized) return;
                            const formattedAward = formatCommentSheetAward(scoring.finalAward);
                            if (!targetTiers.includes(formattedAward)) return;

                            const ageCategory = reg.PerformanceCategory === 'Ensemble'
                                ? 'Ensemble'
                                : (getAgeCategoryLabel(reg.ageCategory) || reg.ageCategory || '—');

                            evaluatedWinners.push({
                                registrant: reg,
                                name: getPerformerName(reg),
                                scores,
                                ageCategory,
                                average: scoring.finalAverageScore,
                                award: scoring.finalAward,
                                formattedAward,
                            });
                        });

                        if (evaluatedWinners.length === 0) return;

                        totalWinnersFound += evaluatedWinners.length;

                        // Get juries for headers and column ordering
                        const { sortedJurySlugs } = getExportJuries(catJuries, evaluatedWinners);

                        const headers = [
                            'name',
                            'award',
                            'category',
                            'score',
                            ...sortedJurySlugs.map((slug, idx) => `header_${slug || (idx + 1)}`),
                        ];

                        const catFolder = (category || 'ALL').replace(/[^a-zA-Z0-9]+/g, '_');

                        // Split into files per award tier
                        targetTiers.forEach((tier) => {
                            const tierRows = evaluatedWinners.filter((w) => w.formattedAward === tier);
                            if (tierRows.length === 0) return;

                            const csvRows = tierRows.map((row) => {
                                const feedbackValues = sortedJurySlugs.map((slug) => {
                                    const s = (row.scores || []).find((score) => {
                                        const sSlug = toJurySlug(score.juryName);
                                        return sSlug === slug;
                                    });
                                    const rawComment = s ? (s.comment || '').trim() : '';
                                    const cleanComment = rawComment.replace(/[\r\n]+/g, ' ').trim();
                                    return escapeCSV(cleanComment, cleanComment.length > 0);
                                });

                                const name = row.name && row.name !== 'Unknown' ? row.name : (getPerformerName(row.registrant) || row.registrant?.name || '');
                                const award = row.formattedAward;
                                const categoryName = formatCommentSheetCategory(row, category);
                                const score = formatCommentSheetScore(row.average);

                                const rowData = [
                                    escapeCSV(name),
                                    escapeCSV(award),
                                    escapeCSV(categoryName),
                                    score,
                                    ...feedbackValues,
                                ];
                                return rowData.join(',');
                            });

                            const csvContent = [headers.join(','), ...csvRows].join('\n');
                            const tierUpper = tier.toUpperCase();
                            const fileName = `DESIGN_TEAM_${catFolder.toUpperCase()}_${tierUpper}_${dateStr}_COMMENT_SHEET.csv`;

                            // Add to ZIP: <Competition_Category>/<Award_Tier>/<File>.csv
                            zip.file(`${catFolder}/${tier}/${fileName}`, csvContent);
                            totalFilesAdded += 1;
                        });
                    } catch (catErr) {
                        console.error(`Error compiling winner sheets for ${category}:`, catErr);
                    }
                })
            );

            if (totalFilesAdded === 0) {
                message.warning({
                    content: 'No finalized comment sheets (Sapphire, Diamond, Gold, Silver, Fail) found across any category to export',
                    key: 'winnerExportZip',
                    duration: 4,
                });
                return;
            }

            // Generate ZIP and trigger download
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            const zipFileName = `WINNER_COMMENT_SHEETS_${selectedEvent}_${dateStr}.zip`;
            saveAs(zipBlob, zipFileName);

            message.success({
                content: `Exported ${totalFilesAdded} winner comment sheet file(s) across categories (${totalWinnersFound} total winners) to ${zipFileName}`,
                key: 'winnerExportZip',
                duration: 4,
            });
        } catch (error) {
            console.error('Batch winner comment sheet export failed:', error);
            message.error({
                content: 'Failed to export winner comment sheets',
                key: 'winnerExportZip',
                duration: 3,
            });
        } finally {
            setBatchExporting(false);
        }
    };

    // --- Expanded row renderer ---
    const expandedRowRender = (record) => {
        if (record.scores.length === 0) {
            return (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No jury scores submitted yet"
                    style={{ padding: '16px 0' }}
                />
            );
        }

        return (
            <div style={{ padding: '8px 0' }}>
                <Row gutter={[16, 16]}>
                    {record.scores.map((s) => {
                        const effectiveScore = getEffectiveScore(s);
                        const isEditing = editingKey === s.docId;
                        const hasAdminAdjustment = s.adminAdjustedScore !== undefined && s.adminAdjustedScore !== null;

                        return (
                            <Col xs={24} lg={12} key={s.docId}>
                                <Card
                                    size="small"
                                    title={
                                        <Space>
                                            <UserOutlined />
                                            <Text strong>{s.juryName || 'Unknown Jury'}</Text>
                                            {record.isFinalized && (
                                                <Tag icon={<LockOutlined />} color="green">
                                                    Finalized
                                                </Tag>
                                            )}
                                        </Space>
                                    }
                                    extra={
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {s.timestamp
                                                ? dayjs(s.timestamp).format('MMM D, YYYY h:mm A')
                                                : 'No date'}
                                        </Text>
                                    }
                                    style={{
                                        borderLeft: `4px solid ${getScoreColor(effectiveScore)}`,
                                    }}
                                >
                                    {/* Score section */}
                                    <Descriptions column={1} size="small" bordered={false}>
                                        <Descriptions.Item label="Original Score">
                                            <Text
                                                strong
                                                style={{
                                                    fontSize: 18,
                                                    color: getScoreColor(s.score),
                                                }}
                                            >
                                                {s.score}/100
                                            </Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Admin Adjusted Score">
                                            {isEditing ? (
                                                <Space>
                                                    <InputNumber
                                                        min={0}
                                                        max={100}
                                                        value={editingValue}
                                                        onChange={setEditingValue}
                                                        style={{ width: 80 }}
                                                        autoFocus
                                                    />
                                                    <Button
                                                        type="primary"
                                                        size="small"
                                                        onClick={() => handleSaveEdit(s.docId)}
                                                        loading={savingEdit}
                                                    >
                                                        Save
                                                    </Button>
                                                    {hasAdminAdjustment && (
                                                        <Popconfirm
                                                            title="Remove adjustment and revert to jury score?"
                                                            onConfirm={() => handleRemoveAdjustment(s.docId)}
                                                            okText="Yes"
                                                            cancelText="No"
                                                        >
                                                            <Button size="small" danger loading={savingEdit}>
                                                                Remove
                                                            </Button>
                                                        </Popconfirm>
                                                    )}
                                                    <Button
                                                        size="small"
                                                        onClick={handleCancelEdit}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </Space>
                                            ) : (
                                                <Space>
                                                    {hasAdminAdjustment ? (
                                                        <Text
                                                            strong
                                                            style={{
                                                                fontSize: 18,
                                                                color: getScoreColor(s.adminAdjustedScore),
                                                            }}
                                                        >
                                                            {s.adminAdjustedScore}/100
                                                        </Text>
                                                    ) : (
                                                        <Text type="secondary">Not adjusted</Text>
                                                    )}
                                                    <Tooltip title="Adjust this jury's score (jury won't see this change)">
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            icon={<EditOutlined />}
                                                            onClick={() =>
                                                                handleStartEdit(
                                                                    s.docId,
                                                                    s.adminAdjustedScore,
                                                                    s.score
                                                                )
                                                            }
                                                        />
                                                    </Tooltip>
                                                    {hasAdminAdjustment && (
                                                        <Text
                                                            type="secondary"
                                                            style={{ fontSize: 11 }}
                                                        >
                                                            by {s.adminAdjustedBy}
                                                        </Text>
                                                    )}
                                                </Space>
                                            )}
                                        </Descriptions.Item>
                                    </Descriptions>

                                    {/* Performance feedback */}
                                    <div style={{ marginTop: 12 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                                            <Text
                                                type="secondary"
                                                style={{ fontSize: 12 }}
                                            >
                                                Performance Feedback (for performer):
                                            </Text>
                                            {!record.isFinalized && editingCommentKey !== s.docId && (
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<EditOutlined />}
                                                    onClick={() => handleStartEditComment(s.docId, s.comment)}
                                                >
                                                    Edit
                                                </Button>
                                            )}
                                        </div>
                                        {editingCommentKey === s.docId ? (
                                            <div>
                                                <Input.TextArea
                                                    value={editingCommentValue}
                                                    onChange={(e) => setEditingCommentValue(e.target.value)}
                                                    rows={4}
                                                    style={{ marginBottom: 8 }}
                                                    autoFocus
                                                />
                                                <Space>
                                                    <Button
                                                        type="primary"
                                                        size="small"
                                                        onClick={() => handleSaveComment(s.docId)}
                                                        loading={savingComment}
                                                    >
                                                        Save
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        onClick={handleCancelEditComment}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </Space>
                                            </div>
                                        ) : (
                                            s.comment ? (
                                                <Paragraph
                                                    style={{
                                                        background: '#f5f5f5',
                                                        padding: 12,
                                                        borderRadius: 6,
                                                        borderLeft: '3px solid #EBBC64',
                                                        marginBottom: 0,
                                                        whiteSpace: 'pre-wrap',
                                                    }}
                                                >
                                                    {s.comment}
                                                </Paragraph>
                                            ) : (
                                                <Text type="secondary" italic>No feedback yet</Text>
                                            )
                                        )}
                                    </div>

                                    {/* APCS team notes */}
                                    <div style={{ marginTop: 12 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                                            <Text
                                                type="secondary"
                                                style={{ fontSize: 12 }}
                                            >
                                                Notes (for APCS team):
                                            </Text>
                                            {!record.isFinalized && editingPanelCommentKey !== s.docId && (
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<EditOutlined />}
                                                    onClick={() => handleStartEditPanelComment(s.docId, s.panelComment)}
                                                >
                                                    Edit
                                                </Button>
                                            )}
                                        </div>
                                        {editingPanelCommentKey === s.docId ? (
                                            <div>
                                                <Input.TextArea
                                                    value={editingPanelCommentValue}
                                                    onChange={(e) => setEditingPanelCommentValue(e.target.value)}
                                                    rows={4}
                                                    style={{ marginBottom: 8 }}
                                                    autoFocus
                                                />
                                                <Space>
                                                    <Button
                                                        type="primary"
                                                        size="small"
                                                        onClick={() => handleSavePanelComment(s.docId)}
                                                        loading={savingComment}
                                                    >
                                                        Save
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        onClick={handleCancelEditPanelComment}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </Space>
                                            </div>
                                        ) : (
                                            s.panelComment ? (
                                                <Paragraph
                                                    style={{
                                                        background: '#f0f5ff',
                                                        padding: 12,
                                                        borderRadius: 6,
                                                        borderLeft: '3px solid #1890ff',
                                                        marginBottom: 0,
                                                        whiteSpace: 'pre-wrap',
                                                    }}
                                                >
                                                    {s.panelComment}
                                                </Paragraph>
                                            ) : (
                                                <Text type="secondary" italic>No notes yet</Text>
                                            )
                                        )}
                                    </div>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>

                {/* Average summary in expanded */}
                <Card
                    size="small"
                    style={{ marginTop: 12, background: '#fafafa' }}
                >
                    <Row gutter={24} align="middle">
                        <Col>
                            <Statistic
                                title="Average Score (effective)"
                                value={record.average !== null ? record.average : '—'}
                                suffix={record.average !== null ? '/ 100' : ''}
                                valueStyle={{
                                    color: getScoreColor(record.average),
                                    fontSize: 24,
                                }}
                            />
                        </Col>
                        {record.totalPenaltyScore > 0 && (
                            <Col>
                                <Statistic
                                    title="Penalty Applied"
                                    value={`-${record.totalPenaltyScore}`}
                                    valueStyle={{ color: '#ff4d4f', fontSize: 18 }}
                                    prefix={<WarningOutlined />}
                                />
                                {record.manualPenaltyScore > 0 && (
                                    <div style={{ fontSize: 11, color: '#888', marginTop: -4 }}>{record.manualPenaltyReason} (-{record.manualPenaltyScore})</div>
                                )}
                                {record.autoPenaltyScore > 0 && (
                                    <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{record.autoPenaltyReason} (-{record.autoPenaltyScore})</div>
                                )}
                            </Col>
                        )}
                        {record.videoPenaltyRuleStatus !== 'matched' && (
                            <Col flex="auto">
                                <Alert
                                    type="warning"
                                    showIcon
                                    message={record.videoPenaltyRuleStatus === 'missing-config'
                                        ? t('videoPenalty.noConfig', {
                                            eventId: record.registrant.eventId || selectedEvent,
                                        })
                                        : t('videoPenalty.noRule')}
                                    description={t('videoPenalty.noAutomaticDeduction')}
                                />
                            </Col>
                        )}
                        <Col>
                            <Statistic
                                title="Jury Members"
                                value={record.juryCount}
                                prefix={<TeamOutlined />}
                            />
                        </Col>
                        <Col flex="auto" style={{ textAlign: 'right' }}>
                            <Space wrap>
                                {record.manualPenaltyScore > 0 ? (
                                        <>
                                            <Popconfirm
                                                title={`Remove manual penalty (-${record.manualPenaltyScore})?`}
                                                onConfirm={() => handleRemovePenalty(record.key)}
                                            >
                                                <Button danger loading={penalizingId === record.key}>
                                                    Remove Penalty (-{record.manualPenaltyScore})
                                                </Button>
                                            </Popconfirm>
                                            {record.manualPenaltyScore === 10 ? (
                                                <Popconfirm
                                                    title="Change penalty to -5 for reading a book?"
                                                    onConfirm={() => handleApplyPenalty(record.key, 5)}
                                                >
                                                    <Button danger icon={<MinusCircleOutlined />} loading={penalizingId === record.key}>
                                                        Change to -5
                                                    </Button>
                                                </Popconfirm>
                                            ) : (
                                                <Popconfirm
                                                    title="Change penalty to -10 for reading a book?"
                                                    onConfirm={() => handleApplyPenalty(record.key, 10)}
                                                >
                                                    <Button danger icon={<MinusCircleOutlined />} loading={penalizingId === record.key}>
                                                        Change to -10
                                                    </Button>
                                                </Popconfirm>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <Popconfirm
                                                title="Apply -5 penalty for reading a book?"
                                                onConfirm={() => handleApplyPenalty(record.key, 5)}
                                            >
                                                <Button danger icon={<MinusCircleOutlined />} loading={penalizingId === record.key}>
                                                    Penalize (-5)
                                                </Button>
                                            </Popconfirm>
                                            <Popconfirm
                                                title="Apply -10 penalty for reading a book?"
                                                onConfirm={() => handleApplyPenalty(record.key, 10)}
                                            >
                                                <Button danger icon={<MinusCircleOutlined />} loading={penalizingId === record.key}>
                                                    Penalize (-10)
                                                </Button>
                                            </Popconfirm>
                                        </>
                                    )}
                                {record.isFinalized ? (
                                    <Popconfirm
                                        title="Unfinalize this registrant?"
                                        description="Jury members will be able to edit their scores again."
                                        onConfirm={() => handleToggleFinalize(record.key, false)}
                                        okText="Yes, unfinalize"
                                    >
                                        <Button
                                            icon={<UnlockOutlined />}
                                            loading={finalizingId === record.key}
                                        >
                                            Unfinalize
                                        </Button>
                                    </Popconfirm>
                                ) : (
                                    <Popconfirm
                                        title="Finalize this registrant's scores?"
                                        description="Jury members will no longer be able to edit their scores for this registrant."
                                        onConfirm={() => handleToggleFinalize(record.key, true)}
                                        okText="Yes, finalize"
                                        okButtonProps={{ type: 'primary' }}
                                        disabled={record.juryCount === 0}
                                    >
                                        <Button
                                            type="primary"
                                            icon={<LockOutlined />}
                                            loading={finalizingId === record.key}
                                            disabled={record.juryCount === 0}
                                        >
                                            Mark as Finalized
                                        </Button>
                                    </Popconfirm>
                                )}
                            </Space>
                        </Col>
                    </Row>
                </Card>
            </div>
        );
    };

    // --- Table columns ---
    const columns = [
        {
            title: 'Participant',
            dataIndex: 'name',
            key: 'name',
            render: (name, record) => (
                <Space align="start">
                    <div
                        style={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            background: '#EBBC64',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 600,
                            fontSize: 12,
                            flexShrink: 0,
                            marginTop: 2,
                        }}
                    >
                        {name
                            .split(' ')
                            .map((w) => w[0])
                            .join('')
                            .slice(0, 2)
                            .toUpperCase()}
                    </div>
                    <div>
                        <Text strong>{name}</Text>
                        <br />
                        <Tag
                            color={
                                record.performanceCategory === 'Ensemble'
                                    ? 'purple'
                                    : 'blue'
                            }
                            style={{ fontSize: 10 }}
                        >
                            {record.performanceCategory === 'Ensemble'
                                ? `Ensemble · ${record.registrant.performers?.length || 0} members`
                                : 'Individual'}
                        </Tag>
                        {record.registrant.videoPerformanceS3Link && (
                            <div style={{ marginTop: 4 }}>
                                <Button
                                    type="link"
                                    size="small"
                                    icon={<PlayCircleOutlined />}
                                    onClick={() => {
                                        handleWatchVideo(record.registrant.videoPerformanceS3Link);
                                    }}
                                    style={{ padding: 0 }}
                                >
                                    Watch Video
                                </Button>
                            </div>
                        )}
                        <div style={{ marginTop: 4, fontSize: 11 }}>
                            <Text type="secondary">Video duration: {formatDuration(getVideoDurationSeconds(record.registrant))}</Text>
                        </div>
                        <div style={{ marginTop: 4, fontSize: 11 }}>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                Song:{' '}
                            </Text>
                            <Text style={{ fontSize: 11, color: record.songName !== '—' ? '#262626' : '#8c8c8c' }}>
                                {record.songName}
                            </Text>
                        </div>
                        <div style={{ marginTop: 4, fontSize: 11 }}>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                Teacher:{' '}
                            </Text>
                            <Text style={{ fontSize: 11, color: record.teacherName !== '—' ? '#262626' : '#8c8c8c' }}>
                                {record.teacherName}
                            </Text>
                        </div>
                        <div style={{ marginTop: 4, fontSize: 11, display: 'flex', alignItems: 'flex-start', gap: 4, flexWrap: 'wrap' }}>
                            <Text type="secondary" style={{ fontSize: 11, flexShrink: 0 }}>
                                Admin Note:{' '}
                            </Text>
                            {record.adminNote ? (
                                <span
                                    style={{
                                        fontSize: 11,
                                        color: '#d46b08',
                                        background: '#fffbe6',
                                        border: '1px solid #ffe58f',
                                        padding: '1px 6px',
                                        borderRadius: 4,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        maxWidth: 240,
                                        display: 'inline-block',
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {record.adminNote}
                                </span>
                            ) : (
                                <span style={{ fontSize: 11, color: '#8c8c8c' }}>—</span>
                            )}
                            <Button
                                type="link"
                                size="small"
                                icon={<EditOutlined style={{ fontSize: 10 }} />}
                                onClick={() => handleOpenAdminNoteModal(record.registrant, record.adminNote)}
                                style={{ padding: '0 4px', fontSize: 11, height: 'auto', lineHeight: 'inherit' }}
                            >
                                {record.adminNote ? 'Edit' : 'Add Note'}
                            </Button>
                        </div>
                    </div>
                </Space>
            ),
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: 'Age Category',
            dataIndex: 'ageCategory',
            key: 'ageCategory',
            width: 150,
            render: (val) => <Text>{val}</Text>,
        },
        {
            title: 'Jury Scores',
            key: 'juryScores',
            width: 280,
            render: (_, record) => {
                if (record.scores.length === 0) {
                    return <Text type="secondary">No scores</Text>;
                }
                return (
                    <Space wrap size={[4, 4]}>
                        {record.scores.map((s) => {
                            const effective = getEffectiveScore(s);
                            const hasAdj =
                                s.adminAdjustedScore !== undefined &&
                                s.adminAdjustedScore !== null;
                            return (
                                <Tooltip
                                    key={s.docId}
                                    title={
                                        hasAdj
                                            ? `${s.juryName}: Original ${s.score}, Adjusted ${s.adminAdjustedScore}`
                                            : `${s.juryName}: ${s.score}/100`
                                    }
                                >
                                    <Tag
                                        color={getScoreColor(effective) === '#52c41a' ? 'success'
                                            : getScoreColor(effective) === '#faad14' ? 'warning'
                                                : getScoreColor(effective) === '#ff7a45' ? 'orange'
                                                    : 'error'}
                                        style={{ fontSize: 11 }}
                                    >
                                        {(s.juryName || 'Jury').split(' ').slice(-1)[0]}:{' '}
                                        <strong>{effective}</strong>
                                        {hasAdj && ' *'}
                                    </Tag>
                                </Tooltip>
                            );
                        })}
                    </Space>
                );
            },
        },
        {
            title: 'Average',
            dataIndex: 'average',
            key: 'average',
            width: 100,
            sorter: (a, b) => (a.average ?? -1) - (b.average ?? -1),
            defaultSortOrder: 'descend',
            render: (avg) =>
                avg !== null ? (
                    <Text
                        strong
                        style={{
                            fontSize: 18,
                            color: getScoreColor(avg),
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {avg}
                    </Text>
                ) : (
                    <Text type="secondary">—</Text>
                ),
        },
        {
            title: 'Award',
            key: 'award',
            width: 120,
            align: 'center',
            render: (_, record) => {
                const award = record.average !== null ? calculateAward(record.average) : 'N/A';
                let color = 'default';
                const awardLower = String(award).toLowerCase();
                if (awardLower.includes('sapphire')) color = 'purple';
                else if (awardLower.includes('diamond')) color = 'cyan';
                else if (awardLower.includes('gold')) color = 'gold';
                else if (awardLower.includes('silver')) color = 'geekblue';
                else if (awardLower.includes('bronze')) color = 'orange';

                if (award === 'N/A') return <Tag>-</Tag>;

                return (
                    <Space direction="vertical" size={2} style={{ display: 'flex', alignItems: 'center' }}>
                        <Tag color={color} style={{ fontWeight: 700, borderRadius: '4px', textTransform: 'uppercase', margin: 0 }}>
                            {award}
                        </Tag>
                        {record.isFinalized && (
                            <div style={{ fontSize: '10px', color: '#52c41a', display: 'flex', alignItems: 'center', gap: '2px', marginTop: '2px' }}>
                                <CheckCircleOutlined style={{ fontSize: '10px' }} /> {t('videoPenalty.juryLocked')}
                            </div>
                        )}
                        {record.needsAwardSync && (
                            <Tag color="warning" style={{ margin: 0, fontSize: '10px' }}>
                                {t('videoPenalty.needsAwardSync')}
                            </Tag>
                        )}
                    </Space>
                );
            }
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 130,
            filters: [
                { text: 'Not Scored', value: 'Not Scored' },
                { text: 'Scored', value: 'Scored' },
                { text: 'Finalized', value: 'Finalized' },
            ],
            onFilter: (value, record) => record.status === value,
            render: (status) => {
                if (status === 'Finalized') {
                    return (
                        <Tag icon={<CheckCircleOutlined />} color="success">
                            Finalized ✅
                        </Tag>
                    );
                }
                if (status === 'Scored') {
                    return <Tag color="processing">Scored</Tag>;
                }
                return <Tag color="default">Not Scored</Tag>;
            },
        },
        {
            title: 'Juries',
            dataIndex: 'juryCount',
            key: 'juryCount',
            width: 70,
            align: 'center',
            render: (count) => (
                <Badge
                    count={count}
                    showZero
                    style={{ backgroundColor: count > 0 ? '#52c41a' : '#d9d9d9' }}
                />
            ),
        },
    ];

    return (
        <Content style={{ padding: '24px 50px', minHeight: 280 }}>
            {/* Header */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: 20,
                }}
            >
                <div>
                    <Title level={2} style={{ marginBottom: 4 }}>
                        <TrophyOutlined style={{ marginRight: 10 }} />
                        Scoring Recap
                    </Title>
                    <Text type="secondary">
                        Review jury scores, adjust if needed, and finalize results.
                        Finalized scores are locked and jury members can no longer edit them.
                    </Text>
                </div>
                <Space wrap>
                    <Select
                        value={selectedEvent}
                        onChange={(val) => {
                            setSelectedEvent(val);
                            setSearchText('');
                            setStatusFilter('All');
                            setAdminNoteFilter('All');
                            setSelectedJuryFilter(null);
                            setActiveCategory('All');
                        }}
                        options={[
                            { label: 'APCS 2025', value: 'APCS2025' },
                            { label: 'APCS 2026', value: 'APCS2026' },
                        ]}
                        style={{ width: 140 }}
                        size="large"
                    />
                    <Select
                        placeholder="Select competition category"
                        value={selectedCategory}
                        onChange={(val) => {
                            setSelectedCategory(val);
                            setSearchText('');
                            setStatusFilter('All');
                            setAdminNoteFilter('All');
                            setSelectedJuryFilter(null);
                            setActiveCategory('All');
                        }}
                        options={categoryOptions}
                        style={{ width: 220 }}
                        showSearch
                        size="large"
                    />
                    <Tooltip title="Batch export finalized comment sheets (Sapphire, Diamond, Gold, Silver, Fail) across all competition categories for this event into a ZIP folder">
                        <Button
                            type="primary"
                            icon={<FileZipOutlined />}
                            loading={batchExporting}
                            onClick={handleExportAllWinnersByAward}
                            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                            size="large"
                        >
                            Export Winner Comment Sheets by Award
                        </Button>
                    </Tooltip>
                </Space>
            </div>

            {!selectedCategory ? (
                <Card style={{ textAlign: 'center', padding: 60 }}>
                    <Empty
                        image={<TrophyOutlined style={{ fontSize: 64, color: '#EBBC64' }} />}
                        description={
                            <span style={{ fontSize: 16 }}>
                                Select a competition category above to view scoring recap
                            </span>
                        }
                    />
                </Card>
            ) : loading ? (
                <div style={{ textAlign: 'center', padding: 80 }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16 }}>Loading scores for {selectedCategory}...</div>
                </div>
            ) : (
                <>
                    {/* Stats row */}
                    <Row gutter={16} style={{ marginBottom: 20 }}>
                        <Col xs={24} sm={6}>
                            <Card size="small">
                                <Statistic
                                    title="Total Registrants"
                                    value={stats.total}
                                    prefix={<UserOutlined />}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={6}>
                            <Card size="small">
                                <Statistic
                                    title="Scored"
                                    value={stats.scored}
                                    prefix={<StarOutlined />}
                                    valueStyle={{ color: '#1890ff' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={6}>
                            <Card size="small">
                                <Statistic
                                    title="Not Scored"
                                    value={stats.notScored}
                                    valueStyle={{
                                        color: stats.notScored > 0 ? '#ff4d4f' : '#52c41a',
                                    }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={6}>
                            <Card size="small">
                                <Statistic
                                    title="Finalized"
                                    value={stats.finalized}
                                    prefix={<CheckCircleOutlined />}
                                    valueStyle={{ color: '#52c41a' }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Jury Progress Section */}
                    {juryProgress.length > 0 && (
                        <Card 
                            size="small" 
                            title={<span><TeamOutlined /> Jury Completion Progress <Text type="secondary" style={{fontSize: 12, fontWeight: 'normal', marginLeft: 8}}>(Click a jury to filter registrants)</Text></span>} 
                            style={{ marginBottom: 16 }}
                            extra={
                                <Button 
                                    type="primary" 
                                    size="small" 
                                    icon={<MailOutlined />}
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleSendReminder();
                                    }}
                                >
                                    Send Reminder (Dummy)
                                </Button>
                            }
                        >
                            <List
                                grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 4, xl: 4, xxl: 4 }}
                                dataSource={juryProgress}
                                renderItem={(jury) => (
                                    <List.Item>
                                        <Card 
                                            size="small" 
                                            style={{ 
                                                borderColor: selectedJuryFilter === jury.id ? '#1890ff' : (jury.isComplete ? '#b7eb8f' : '#f0f0f0'),
                                                borderWidth: selectedJuryFilter === jury.id ? 2 : 1,
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => setSelectedJuryFilter(prev => prev === jury.id ? null : jury.id)}
                                        >
                                            <List.Item.Meta
                                                avatar={<Avatar style={{ backgroundColor: jury.isComplete ? '#52c41a' : '#1890ff' }}>{jury.name.charAt(0).toUpperCase()}</Avatar>}
                                                title={jury.name}
                                                description={
                                                    <div style={{ marginTop: 8 }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                                {jury.scoredCount} / {jury.targetCount} scored
                                                            </Text>
                                                            {jury.isComplete && (
                                                                <Tag color="success" style={{ margin: 0, fontSize: 10 }}>Done</Tag>
                                                            )}
                                                        </div>
                                                        <Progress 
                                                            percent={jury.percent} 
                                                            size="small" 
                                                            status={jury.isComplete ? 'success' : 'active'}
                                                            strokeColor={jury.isComplete ? '#52c41a' : '#1890ff'}
                                                        />
                                                    </div>
                                                }
                                            />
                                        </Card>
                                    </List.Item>
                                )}
                            />
                        </Card>
                    )}

                    {/* Info alert */}
                    <Alert
                        message="How admin score adjustments work"
                        description="When you adjust a jury's score, the change is stored separately — the jury member still sees their original score. The average is calculated using the adjusted score when available."
                        type="info"
                        showIcon
                        closable
                        style={{ marginBottom: 16 }}
                    />

                    {/* Filter bar */}
                    <Card size="small" style={{ marginBottom: 16 }}>
                        {categories.length > 1 && (
                            <Tabs
                                activeKey={activeCategory}
                                onChange={(key) => setActiveCategory(key)}
                                style={{ marginBottom: 12 }}
                                items={categories.map((c) => {
                                    const count = categoryCounts.total[c] || 0;
                                    const notScored = categoryCounts.notScored[c] || 0;
                                    const active = activeCategory === c;
                                    return {
                                        key: c,
                                        label: (
                                            <Space size={6} align="center">
                                                <span style={{ fontWeight: active ? 600 : 400 }}>{c}</span>
                                                <Badge
                                                    count={count}
                                                    showZero
                                                    overflowCount={999}
                                                    style={{
                                                        backgroundColor: active ? '#1890ff' : '#f0f0f0',
                                                        color: active ? '#ffffff' : '#595959',
                                                        boxShadow: 'none',
                                                        fontSize: 11,
                                                        padding: '0 6px',
                                                        height: 18,
                                                        lineHeight: '18px',
                                                        borderRadius: 9,
                                                    }}
                                                />
                                                {notScored > 0 && (
                                                    <Tooltip title={`${notScored} not scored`}>
                                                        <Badge
                                                            count={notScored}
                                                            style={{
                                                                backgroundColor: '#ff4d4f',
                                                                boxShadow: 'none',
                                                                fontSize: 10,
                                                                height: 16,
                                                                minWidth: 16,
                                                                lineHeight: '16px',
                                                                borderRadius: 8,
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Space>
                                        ),
                                    };
                                })}
                            />
                        )}
                        <Row gutter={12} align="middle">
                            <Col flex="auto">
                                <Input
                                    placeholder="Search by participant, teacher, song, or admin note..."
                                    prefix={<SearchOutlined />}
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    allowClear
                                />
                            </Col>
                            <Col>
                                <Select
                                    value={statusFilter}
                                    onChange={setStatusFilter}
                                    style={{ width: 150 }}
                                    options={[
                                        { label: 'All Statuses', value: 'All' },
                                        { label: 'Scored', value: 'Scored' },
                                        { label: 'Not Scored', value: 'Not Scored' },
                                        { label: 'Finalized', value: 'Finalized' },
                                    ]}
                                />
                            </Col>
                            <Col>
                                <Select
                                    value={adminNoteFilter}
                                    onChange={setAdminNoteFilter}
                                    style={{ width: 160 }}
                                    options={[
                                        { label: 'All Admin Notes', value: 'All' },
                                        { label: 'Has Admin Note', value: 'NOT_EMPTY' },
                                        { label: 'No Admin Note', value: 'EMPTY' },
                                    ]}
                                />
                            </Col>
                            {selectedJuryFilter && (
                                <Col>
                                    <Tag closable onClose={() => setSelectedJuryFilter(null)} color="blue" style={{ padding: '4px 8px', fontSize: 14 }}>
                                        Filtered by Jury: {juries.find(j => j.id === selectedJuryFilter)?.name || 'Unknown'}
                                    </Tag>
                                </Col>
                            )}
                            <Col>
                                <Space wrap>
                                    <Button
                                        icon={<DownloadOutlined />}
                                        onClick={() => handleExportCSV({ isVariableSafe: false })}
                                        disabled={filteredData.length === 0}
                                    >
                                        Export CSV
                                    </Button>
                                    <Tooltip title="Exports: name, award, category, score, feedback1, feedback2...">
                                        <Button
                                            type="primary"
                                            icon={<FileDoneOutlined />}
                                            onClick={() => handleExportCSV({ isVariableSafe: true })}
                                            disabled={filteredData.length === 0}
                                            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                                        >
                                            Export Comment Sheet CSV
                                        </Button>
                                    </Tooltip>
                                </Space>
                            </Col>
                        </Row>
                    </Card>

                    {/* Main table */}
                    <Card bodyStyle={{ padding: 0 }}>
                        <Table
                            columns={columns}
                            dataSource={filteredData}
                            expandable={{
                                expandedRowRender,
                                expandRowByClick: true,
                                expandIcon: ({ expanded, onExpand, record }) => (
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<EyeOutlined />}
                                        onClick={(e) => onExpand(record, e)}
                                        style={{
                                            color: expanded ? '#1890ff' : undefined,
                                        }}
                                    >
                                        {expanded ? 'Hide' : 'Review'}
                                    </Button>
                                ),
                            }}
                            pagination={{
                                pageSize: 15,
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '15', '25', '50'],
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} of ${total} registrants`,
                            }}
                            rowClassName={(record) =>
                                record.isFinalized ? 'scoring-recap-finalized-row' : ''
                            }
                            size="middle"
                            scroll={{ x: 900 }}
                            locale={{
                                emptyText: (
                                    <Empty
                                        description={
                                            activeCategory !== 'All'
                                                ? `No registrants found for ${selectedCategory} (${activeCategory})`
                                                : `No registrants found for ${selectedCategory}`
                                        }
                                    />
                                ),
                            }}
                        />
                    </Card>

                    {/* Legend */}
                    <div style={{ marginTop: 12, fontSize: 12, color: '#999' }}>
                        <Space split="·">
                            <span>* = admin adjusted score</span>
                            <span>Average uses adjusted score when available</span>
                            <span>Finalized scores are locked from jury editing</span>
                        </Space>
                    </div>
                </>
            )}

            {/* Minimal inline styles for finalized row */}
            <style>{`
                .scoring-recap-finalized-row {
                    background: #f6ffed !important;
                }
                .scoring-recap-finalized-row td {
                    opacity: 0.85;
                }
            `}</style>

            <Modal
                title="Performance Video"
                open={videoModalOpen}
                onCancel={() => {
                    setVideoModalOpen(false);
                    setCurrentVideo(null);
                }}
                footer={null}
                width={800}
                destroyOnClose
            >
                {currentVideo && (
                    <video
                        controls
                        controlsList="nodownload"
                        playsInline
                        preload="metadata"
                        style={{ width: '100%', maxHeight: '70vh', backgroundColor: '#000' }}
                        src={currentVideo}
                    />
                )}
            </Modal>

            {/* Admin Note Modal */}
            <Modal
                title={
                    <Space>
                        <EditOutlined />
                        <span>Edit Admin Note{adminNoteRecord ? ` — ${getPerformerName(adminNoteRecord)}` : ''}</span>
                    </Space>
                }
                open={isAdminNoteModalVisible}
                onOk={handleSaveAdminNote}
                confirmLoading={savingAdminNote}
                onCancel={() => {
                    setIsAdminNoteModalVisible(false);
                    setAdminNoteRecord(null);
                }}
                okText="Save"
                cancelText="Cancel"
                destroyOnClose
            >
                <div style={{ marginBottom: '16px' }}>
                    <Typography.Text type="secondary">
                        Enter a special note for this registrant.
                    </Typography.Text>
                    <Input.TextArea
                        rows={4}
                        placeholder="e.g. Paid with USD rate"
                        value={adminNoteValue}
                        onChange={(e) => setAdminNoteValue(e.target.value)}
                        style={{ marginTop: '8px' }}
                    />
                </div>
            </Modal>
        </Content>
    );
};

export default ScoringRecap;
